import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import AttendLogo from '../assets/attendlogo.png';

const Login: React.FC = () => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const { login, loading, error, clearError, user } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);
  
  // Show either context error or local error
  const displayError = error || localError;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError(t('login.emailRequired'));
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError(t('login.emailInvalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError(t('login.passwordRequired'));
      return false;
    }
    if (password.length < 4) {
      setPasswordError(t('login.passwordMinLength'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    // Validate inputs
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      // Store error in local state as backup
      const errorMessage = err.message || t('login.loginFailed');
      setLocalError(errorMessage);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEmail(newValue);
    // Only clear login error if user is actually typing (value is different)
    if ((error || localError) && newValue !== email) {
      clearError();
      setLocalError(null);
    }
    if (emailError) {
      validateEmail(newValue);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPassword(newValue);
    // Only clear login error if user is actually typing (value is different)
    if ((error || localError) && newValue !== password) {
      clearError();
      setLocalError(null);
    }
    if (passwordError) {
      validatePassword(newValue);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 48,
      backgroundColor: 'var(--an-surface)',
      borderRadius: 'var(--an-radius-sm)',
      transition: 'border-color var(--an-transition), background-color var(--an-transition)',
      '& fieldset': {
        borderColor: 'var(--an-border)',
      },
      '&:hover fieldset': {
        borderColor: 'rgb(var(--an-primary-rgb) / 0.42)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--an-primary)',
        borderWidth: 2,
      },
      '&.Mui-error fieldset': {
        borderColor: 'var(--an-error)',
      },
    },
    '& .MuiInputAdornment-root svg': {
      color: 'var(--an-text-tertiary)',
    },
    '& .MuiInputBase-input': {
      color: 'var(--an-text-primary)',
    },
    '& .MuiInputLabel-root': {
      color: 'var(--an-text-secondary)',
      fontWeight: 700,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'var(--an-primary)',
      backgroundColor: 'var(--an-surface)',
      paddingInline: '0.25rem',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px, 0.92fr) minmax(480px, 1.08fr)' },
        backgroundColor: 'var(--an-background)',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          minHeight: '100dvh',
          position: 'relative',
          overflow: 'hidden',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 2,
          p: { lg: 3.5, xl: 5 },
          color: 'var(--an-sidebar-text)',
          background:
            'radial-gradient(circle at 18% 16%, rgb(var(--an-accent-rgb) / 0.22), var(--an-transparent) 24rem), linear-gradient(180deg, var(--an-sidebar-bg), var(--an-primary-dark))',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgb(var(--an-sidebar-text-rgb) / 0.055) 1px, var(--an-transparent) 1px), linear-gradient(90deg, rgb(var(--an-sidebar-text-rgb) / 0.055) 1px, var(--an-transparent) 1px)',
            backgroundSize: '40px 40px',
            maskImage: 'linear-gradient(to bottom, rgb(var(--an-sidebar-text-rgb) / 0.72), var(--an-transparent))',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 2,
              py: 1.25,
              borderRadius: 'var(--an-radius-md)',
              backgroundColor: 'rgb(var(--an-surface-rgb) / 0.94)',
              border: '1px solid rgb(var(--an-surface-rgb) / 0.64)',
              boxShadow: 'var(--an-shadow-sm)',
            }}
          >
            <Box
              component="img"
              src={AttendLogo}
              alt="AttendNow"
              sx={{ height: 40, width: 'auto', display: 'block' }}
            />
          </Box>
        </Box>

        <Box
          aria-hidden="true"
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'grid',
            gap: { lg: 2, xl: 3 },
            minHeight: 0,
          }}
        >
          <Box sx={{ maxWidth: 520 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                mb: 2.25,
                borderRadius: 'var(--an-radius-sm)',
                color: 'var(--an-accent)',
                backgroundColor: 'rgb(var(--an-accent-rgb) / 0.10)',
                border: '1px solid rgb(var(--an-accent-rgb) / 0.22)',
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: 0,
              }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'var(--an-accent)' }} />
              AI workforce operations
            </Box>
            <Box
              sx={{
                width: '82%',
                height: 20,
                mb: 1.35,
                borderRadius: 99,
                backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.92)',
              }}
            />
            <Box
              sx={{
                width: '62%',
                height: 20,
                mb: 2,
                borderRadius: 99,
                backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.76)',
              }}
            />
            <Box sx={{ display: 'grid', gap: 0.8 }}>
              {[0, 1].map((item) => (
                <Box
                  key={item}
                  sx={{
                    width: item === 0 ? '72%' : '56%',
                    height: 9,
                    borderRadius: 99,
                    backgroundColor: 'rgb(var(--an-sidebar-text-rgb) / 0.30)',
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              height: 'clamp(300px, 50vh, 430px)',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 'auto -3rem -3rem auto',
                width: 260,
                height: 260,
                borderRadius: '50%',
                border: '1px solid rgb(var(--an-sidebar-text-rgb) / 0.12)',
                background: 'rgb(var(--an-surface-rgb) / 0.025)',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                width: 'min(100%, 600px)',
                borderRadius: 'var(--an-radius-md)',
                border: '1px solid rgb(var(--an-surface-rgb) / 0.18)',
                background: 'linear-gradient(145deg, rgb(var(--an-surface-rgb) / 0.18), rgb(var(--an-surface-rgb) / 0.07))',
                boxShadow: 'var(--an-shadow-sm)',
                backdropFilter: 'blur(22px)',
                WebkitBackdropFilter: 'blur(22px)',
                p: 1.15,
              }}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  borderRadius: 'calc(var(--an-radius-lg) - 5px)',
                  backgroundColor: 'rgb(var(--an-surface-rgb) / 0.96)',
                  border: '1px solid rgb(var(--an-surface-rgb) / 0.62)',
                }}
              >
                <Box
                  sx={{
                    height: 54,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    borderBottom: '1px solid var(--an-border-light)',
                    backgroundColor: 'var(--an-surface)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'var(--an-primary)' }} />
                    <Box>
                      <Box sx={{ width: 116, height: 10, mb: 0.8, borderRadius: 99, backgroundColor: 'var(--an-text-primary)' }} />
                      <Box sx={{ width: 160, height: 7, borderRadius: 99, backgroundColor: 'var(--an-border)' }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ width: 74, height: 30, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'var(--an-surface-muted)', border: '1px solid var(--an-border-light)' }} />
                    <Box sx={{ width: 84, height: 30, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'var(--an-primary)' }} />
                  </Box>
                </Box>

                <Box sx={{ p: { lg: 1.35, xl: 2 }, display: 'grid', gap: { lg: 1, xl: 1.5 } }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                    {[0, 1, 2, 3].map((item) => (
                      <Box
                        key={item}
                        sx={{
                          minHeight: { lg: 62, xl: 82 },
                          p: 1.2,
                          borderRadius: 'var(--an-radius-md)',
                          backgroundColor: item === 0 ? 'var(--an-primary-light)' : 'var(--an-surface)',
                          border: '1px solid var(--an-border-light)',
                          boxShadow: 'var(--an-shadow-sm)',
                        }}
                      >
                        <Box sx={{ width: '46%', height: 7, mb: 1.35, borderRadius: 99, backgroundColor: item === 0 ? 'rgb(var(--an-primary-rgb) / 0.24)' : 'var(--an-border)' }} />
                        <Box sx={{ width: item === 2 ? '50%' : '68%', height: 24, borderRadius: 'var(--an-radius-sm)', backgroundColor: item === 0 ? 'var(--an-primary)' : item === 1 ? 'var(--an-accent)' : 'var(--an-neutral-light)' }} />
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 0.75fr', gap: 1.25 }}>
                    <Box
                      sx={{
                        height: { lg: 120, xl: 180 },
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 'var(--an-radius-md)',
                        backgroundColor: 'var(--an-surface)',
                        border: '1px solid var(--an-border-light)',
                        boxShadow: 'var(--an-shadow-sm)',
                      }}
                    >
                      {[0, 1, 2, 3, 4].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            position: 'absolute',
                            left: 16,
                            right: 16,
                            bottom: 24 + item * 28,
                            height: 1,
                            backgroundColor: 'var(--an-border-light)',
                          }}
                        />
                      ))}
                      {[0, 1, 2, 3, 4, 5, 6].map((item) => (
                        <Box
                          key={item}
                          sx={{
                            position: 'absolute',
                            left: `${9 + item * 12}%`,
                            bottom: 24,
                            width: 14,
                            height: `${30 + ((item * 17) % 62)}%`,
                            borderRadius: '999px 999px 0 0',
                            backgroundColor: item === 5 ? 'var(--an-accent)' : 'rgb(var(--an-primary-rgb) / 0.34)',
                          }}
                        />
                      ))}
                    </Box>

                    <Box
                      sx={{
                        height: { lg: 120, xl: 180 },
                        p: { lg: 1, xl: 1.4 },
                        display: 'grid',
                        gap: 1,
                        borderRadius: 'var(--an-radius-md)',
                        backgroundColor: 'var(--an-surface)',
                        border: '1px solid var(--an-border-light)',
                        boxShadow: 'var(--an-shadow-sm)',
                      }}
                    >
                      {[0, 1, 2, 3, 4].map((item) => (
                        <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item === 0 ? 'var(--an-success)' : item === 1 ? 'var(--an-warning)' : 'var(--an-border)' }} />
                          <Box sx={{ flex: 1, height: 7, borderRadius: 99, backgroundColor: 'var(--an-border-light)' }} />
                          <Box sx={{ width: `${26 + item * 8}%`, height: 7, borderRadius: 99, backgroundColor: item === 0 ? 'var(--an-success-light)' : 'var(--an-primary-light)' }} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                right: 18,
                top: { lg: 24, xl: 42 },
                width: 182,
                p: 1.45,
                borderRadius: 'var(--an-radius-md)',
                backgroundColor: 'rgb(var(--an-surface-rgb) / 0.16)',
                border: '1px solid rgb(var(--an-surface-rgb) / 0.18)',
                boxShadow: 'var(--an-shadow-sm)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
              }}
            >
              <Box sx={{ width: '44%', height: 8, mb: 1.25, borderRadius: 99, backgroundColor: 'rgb(var(--an-surface-rgb) / 0.34)' }} />
              <Box sx={{ width: '72%', height: 26, borderRadius: 'var(--an-radius-sm)', backgroundColor: 'rgb(var(--an-accent-rgb) / 0.38)' }} />
            </Box>
          </Box>

          <Box sx={{ height: 1, flexShrink: 0, background: 'rgb(var(--an-sidebar-text-rgb) / 0.16)' }} />
        </Box>
      </Box>

      <Box
        sx={{
          minHeight: '100dvh',
          position: 'relative',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4, lg: 7 },
          py: { xs: 4, lg: 6 },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 80% 12%, rgb(var(--an-primary-rgb) / 0.08), var(--an-transparent) 24rem), radial-gradient(circle at 12% 88%, rgb(var(--an-accent-rgb) / 0.08), var(--an-transparent) 22rem)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Card
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 'var(--an-radius-md)',
              overflow: 'hidden',
              border: '1px solid var(--an-border-light)',
              boxShadow: 'var(--an-shadow-sm)',
              backgroundColor: 'var(--an-surface)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    px: 1.5,
                    py: 1,
                    borderRadius: 'var(--an-radius-md)',
                    border: '1px solid var(--an-border-light)',
                    backgroundColor: 'var(--an-surface)',
                    boxShadow: 'var(--an-shadow-sm)',
                  }}
                >
                  <Box
                    component="img"
                    src={AttendLogo}
                    alt="AttendNow"
                    sx={{ height: 44, width: 'auto', display: 'block' }}
                  />
                </Box>
                <Typography variant="h4" component="h1" gutterBottom fontWeight={800} sx={{ color: 'var(--an-text-primary)' }}>
                  {t('login.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'var(--an-text-secondary)', maxWidth: 420 }}>
                  {t('login.subtitle')}
                </Typography>
              </Box>

              {displayError && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    borderRadius: 'var(--an-radius-sm)',
                    border: '1px solid var(--an-error-light)',
                    backgroundColor: 'var(--an-error-light)',
                    color: 'var(--an-error)',
                    '& .MuiAlert-message': {
                      fontWeight: 700,
                    },
                    '& .MuiAlert-icon': {
                      alignItems: 'center',
                      color: 'var(--an-error)',
                    },
                  }}
                >
                  {displayError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('login.emailLabel')}
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => validateEmail(email)}
                  required
                  margin="normal"
                  error={!!emailError}
                  helperText={emailError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...textFieldSx, mb: 2 }}
                />

                <TextField
                  fullWidth
                  label={t('login.passwordLabel')}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => validatePassword(password)}
                  required
                  margin="normal"
                  error={!!passwordError}
                  helperText={passwordError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          aria-label="toggle password visibility"
                          sx={{ color: 'var(--an-text-secondary)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...textFieldSx, mb: 1 }}
                />

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 800,
                      color: 'var(--an-primary)',
                      textDecoration: 'none',
                      '&:hover': {
                        color: 'var(--an-primary-dark)',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !!emailError || !!passwordError}
                  startIcon={!loading ? <i className="bi bi-box-arrow-in-right"></i> : undefined}
                  sx={{
                    mt: 2,
                    mb: 2,
                    py: 1.35,
                    borderRadius: 'var(--an-radius-sm)',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 800,
                    color: 'var(--an-white)',
                    backgroundColor: 'var(--an-primary)',
                    '&:hover': {
                      backgroundColor: 'var(--an-primary-dark)',
                    },
                    '&:disabled': {
                      color: 'var(--an-text-tertiary)',
                      backgroundColor: 'var(--an-border-light)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('login.signIn')
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;


